import { CreateAllData } from '@/apiError/apiCreate';
import { useDeleteApi } from '@/apiError/apiDelete';
import { useGetApiInfiniteList } from '@/apiError/apiGetList';
import '@/assets/style/companyList.scss';
import ItemForm from '@/components/forms/master/inventory/item/ItemForm';
import CreateModalButton from '@/utils/components/CreateModelButton';
import DeleteModalButton from '@/utils/components/DeleteModalButton';
import EditModalButton from '@/utils/components/EditModalButton';
import ItemUploadButton from '@/utils/components/ItemUploadButton.';
import SearchBox from '@/utils/components/SearchBox';
import GlobalFormModal from '@/utils/modal/GlobalFormModal';
import GlobalModal from '@/utils/modal/Modal';
import {
  fetchPermissions,
  hasPermission,
} from '@/utils/permissions/userPermission';
import useDebounce from '@/hooks/useDebounce';
import DynamicTable from '@/utils/table/DynamicTable';
import { inventoryItemTableHeader as baseColumns } from '@/utils/tableHeader';
// import { EditOutlined } from '@ant-design/icons';
import { Card, Col, Form, Row } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';

/**
 * Item Component
 * Displays and manages inventory items with search, delete, and edit functionalities.
 *
 * @component
 * @returns {React.ReactElement} Inventory management interface.
 */
const Item = () => {
  const [form] = Form.useForm();
  const [loadingEditItem, setLoadingEditItem] = useState(false);
  const [searchTerm, setSearchTrem] = useState('');
  const itemName = useDebounce((searchTerm || '').trim(), 400);
  const [deleteReferenceId, setDeleteReferenceId] = useState(null);
  const [modalMode, setModalMode] = useState('create');
  const [isMessageModalVisible, setIsMessageModalVisible] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalType, setModalType] = useState('');
  const [permissions, setPermissions] = useState([]);
  const [selectedId, setSelectedId] = useState(null); // Holds data for the ledger to edit
  const { mutateAsync: deleteData, isPending: deleteLoading } = useDeleteApi();
  // const [inventoryTypeList, setInventoryTypeList] = useState();

  /**
   * Shows the delete confirmation modal.
   *
   * @param {string} referenceId - The ID of the item to be deleted.
   */
  const showCancelModal = (referenceId) => {
    setModalType('delete');
    setIsMessageModalVisible(true);
    setDeleteReferenceId(referenceId);
  };

  /**
   * Hides the modal.
   */
  const handleCancel = () => {
    setIsMessageModalVisible(false);
  };

  const openModal = (mode, ledger = null) => {
    // fetchItemsGroup(ledger);
    setModalMode(mode);
    if (mode === 'edit' && ledger) {
      setSelectedId(ledger);
    } else {
      setSelectedId(null);
    }
    setIsModalVisible(true);
  };

  const closeModal = () => {
    form.resetFields();
    setSelectedId(null);
    setIsModalVisible(false);
  };
  // const fetchItemsGroup = async (referenceId) => {
  //   try {
  //     setLoadingEditItem(true);
  //     if (referenceId) {
  //       const inventoryDetailsByIdResponse = await ShowDataById.getDataById(
  //         `account/inventory/item/${referenceId}/`,
  //       );
  //       form.setFieldsValue(inventoryDetailsByIdResponse?.data);
  //     }
  //   } finally {
  //     setLoadingEditItem(false);
  //   }
  // };

  const deleteItem = async () => {
    if (!deleteReferenceId) return;
    setIsMessageModalVisible(true);
    try {
      await deleteData(`/account/inventory/item/${deleteReferenceId}/`);
      fetchInventoryItemData();
    } catch (err) {
      console.error(err);
    } finally {
      setIsMessageModalVisible(false);
    }
  };

  const {
    data: inventoryItemData,
    isListLoading: loading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch: fetchInventoryItemData,
  } = useGetApiInfiniteList('account/inventory/items/', {
    params: itemName ? { itemName } : {},
    pageSize: 20,
  });

  /**
   * Handles the search input change.
   *
   * @param {string} value - The search term.
   */
  const handleSearch = (value) => {
    setSearchTrem(value);
  };

  useEffect(() => {
    setPermissions(fetchPermissions());
  }, []);

  const columns = useMemo(() => {
    const cols = [...baseColumns];
    const update = hasPermission(permissions, 'Item', 'isDelete');
    if (
      hasPermission(permissions, 'Item', 'isUpdate') ||
      hasPermission(permissions, 'Item', 'isDelete')
    ) {
      cols.push({
        title: 'Action',
        key: 'action',
        align: 'center',
        width: '5%',
        fixed: 'right',

        render: (_, item) => (
          <div className="ant-action" style={{ display: 'flex' }}>
            {hasPermission(permissions, 'Item', 'isUpdate') && (
              <EditModalButton openModal={openModal} id={item?.referenceId} />
            )}
            {hasPermission(permissions, 'Item', 'isDelete') && (
              <DeleteModalButton
                showCancelModal={showCancelModal}
                data={item?.referenceId}
              />
            )}
          </div>
        ),
      });
    }

    return cols;
  }, [permissions]);

  const handleSubmit = async (data) => {
    try {
      await CreateAllData.createItem('account/inventory/items/', storeValues);
      fetchInventoryItemData();
    } finally {
    }
  };

  return (
    <div style={{ position: 'relative', top: 0, marginTop: 0, paddingTop: 0 }}>
      <div className="table">
        <Row gutter={[24, 0]}>
          <Col xs={24} xl={24}>
            <Card
              variant="borderless"
              className="circlebox tablespace mb-24"
              title={
                <>
                  {hasPermission(permissions, 'Item', 'isCreate') && (
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <CreateModalButton
                        openModal={openModal}
                        title="Create New Item"
                      />
                      <ItemUploadButton refreshTable={fetchInventoryItemData} />
                    </div>
                  )}
                </>
              }
              extra={
                <SearchBox
                  searchPlaceholder="Search item..."
                  handleSearch={handleSearch}
                />
              }
            >
              <div className="table-responsive">
                <DynamicTable
                  data={inventoryItemData?.data ?? []}
                  columns={columns}
                  loading={loading}
                  isShowDynamicScroll={true}
                  scrollY
                  onLoadMore={fetchNextPage}
                  hasMore={!!hasNextPage}
                  loadingMore={isFetchingNextPage}
                />
              </div>
            </Card>
          </Col>
        </Row>
      </div>
      <GlobalFormModal
        isVisible={isModalVisible}
        title={modalMode === 'create' ? 'Create Item' : 'Update Item'}
        onClose={() => {
          closeModal();
          form.resetFields();
        }}
        loading={false}
      >
        <ItemForm
          mode={modalMode}
          referenceId={selectedId}
          onClose={closeModal}
          refreshTable={fetchInventoryItemData}
          // inventoryTypeList={inventoryTypeList}
          form={form}
          loadingEditItem={loadingEditItem}
        />
      </GlobalFormModal>
      <GlobalModal
        isVisible={isMessageModalVisible}
        type={modalType}
        onConfirm={deleteItem}
        onClose={handleCancel}
        loading={deleteLoading}
      />
    </div>
  );
};

export default Item;
